import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, OnInit, Optional } from '@angular/core';
import { NavController, ToastController } from '@ionic/angular';
import { AuthStoreDispatcher } from 'src/app/core/state/auth/auth.dispatcher';
import { Subscription, Subject, BehaviorSubject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { SopoLoadingService } from 'src/app/shared/services/sopo-loading/sopo-loading.service';
import { AnonymusQuickplayComponent } from 'src/app/account/pages/login/anonymus-quickplay/anonymus-quickplay.component';
import { ForgotPasswordComponent } from 'src/app/account/pages/login/component/forgot-password.component';
import { AgreePrivacyComponent } from 'src/app/account/pages/login/agree-privacy/agree-privacy.component';
import { AuthService } from 'src/app/core/firebase/auth.service';
import {
    Validators,
    ValidatorFn,
    AbstractControl,
    FormGroup,
    FormControl,
} from '@angular/forms';
import { GuestLoginPopupComponent } from 'src/app/game/component/guest-login-popup/guest-login-popup.component';
import { ActivatedRoute } from '@angular/router';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { SopoConfirmSettings } from 'src/app/sopo-popup/interfaces/confirm';
import { FeatureService } from 'src/app/core/firebase/feature.service';
import { ProfileService } from 'src/app/core/firebase/profile.service';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { InviteCodeService } from 'src/app/core/firebase/invite-code.service';
import AppLauncherElectron from 'src/app/plugins/app-launcher.plugin';
import { isElectron } from 'src/app/shared/utils/platform.utils';
import { SupportedWallets } from 'src/app/core/state/auth/auth.state';
import { SOPO_DIALOG_DATA, SopoDialogRef } from 'src/app/sopo-popup/interfaces/dialog';
import { ThemeService } from 'src/app/core/firebase/theme.service';

@Component({
    selector: 'app-login-and-registration',
    templateUrl: './login-and-registration.component.html',
    styleUrls: ['./login-and-registration.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LoginAndRegistrationComponent implements OnInit, OnDestroy {
    private isLoading = true;
    private _routeParameterSubscription: Subscription;
    isSignUp = false;
    islogin = false;
    email: any = '';
    password = '';
    confirmPassword = '';
    inviteCode = '';
    authSubscribe: Subscription;
    ngUnsubscribe = new Subject<void>();
    acceptPrivacy = new BehaviorSubject(false);
    acceptTerms = new BehaviorSubject(false);
    needPrivacyAndTerms = false;
    isQuickplay = false;
    loginErroFlag = false;
    registrationErrorFlag = false;
    registrationForm: FormGroup;
    loginForm: FormGroup;
    createTableOption = true;
    isNavigateToLobby = false;
    showLoginInfos = true;
    navigateFrom = false;
    desktopConfig = false;
    isElectronApp = isElectron();
    facebookLoginEnabled = false;
    facebookLoginSubscription: Subscription | undefined;
    theme = 'black-gold-theme';
    validationMessages = {
        email: [
            { type: 'required', message: 'Email is required.' },
            {
                type: 'email',
                message: 'Invalid email address.',
            },
        ],
        password: [
            { type: 'required', message: 'Password is required.' },
            {
                type: 'minlength',
                message: 'The password must be at least 8 characters long'
            },
            {
                type: 'pattern',
                message: 'The password must have at least: 1 number, 1 letter, and 1 symbol.'
            },
        ],
        loginPassword: [
            { type: 'required', message: 'Password is required.' },
            {
                type: 'minlength',
                message: 'The password must be at least 8 characters long'
            },
            {
                type: 'pattern',
                message: 'The password must have at least: 1 number, 1 letter, and 1 symbol.'
            },
        ],
        confirmPassword: [
            { type: 'matching', message: 'Password and confirmation password do not match!' },
        ],
        inviteCode: [
            { type: 'pattern', message: 'Invalid invite code format!'},
            { type: 'validity', message: 'Non existing invite code!'},
            { type: 'network', message: 'Could not verifiy invite code, network error!'},
        ]
    };

    isAuthSubscription: Subscription; 

    constructor(
        private authStoreDispatcher: AuthStoreDispatcher,
        private authService: AuthService,
        private matDialog: MatDialog,
        private iab: InAppBrowser,
        public ref: ChangeDetectorRef,
        private loadingCtrl: SopoLoadingService,
        private navCtrl: NavController,
        private zone: NgZone,
        private route: ActivatedRoute,
        private sopoPopupService: SopoPopupService,
        private featureService: FeatureService,
        private toastCtrl: ToastController,
        private profileService: ProfileService,
        private profileDispatcher: ProfileStateDispatcher,
        private inviteCodeService: InviteCodeService,
        private themeService: ThemeService,
        @Optional() @Inject(SOPO_DIALOG_DATA) public data: any,
        @Optional() @Inject(SopoDialogRef) public dialogRef: SopoDialogRef,
    ) {
        if (data) {
            this.isQuickplay  = data.isQuickplay;
            this.showLoginInfos = data.showLoginInfos;
            this.isNavigateToLobby = data.isNavigateToLobby;
            this.isSignUp = data.isSignUp;
            this.islogin = data.islogin;
            this.email = data.email;
            this.createTableOption = data.createTableOption;
            this.navigateFrom = data.navigateFrom;
        }
        this.authSubscribe = this.authStoreDispatcher
            .getState()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((authState) => {
                this.isLoading = authState.isFetching;
            });
    }

    ngOnInit() {
        this.desktopConfig = this.featureService.isDesktop;
        this.theme = this.themeService.themePath;
        this.registrationForm = new FormGroup({
            email: new FormControl(
                null,
                [
                    Validators.required,
                    Validators.email,
                ]),
            password: new FormControl(
                null,
                [
                    Validators.required,
                    Validators.minLength(8),
                    Validators.pattern(/^(?=.*[a-z])(?=.*[0-9])(?=.*[\^\$\*\.\[\]\{\}\(\)\?\|\\/&"!@#%,><':;_~`])/)
                ]),
            confirmPassword: new FormControl(null),
            inviteCode: new FormControl(
                null,
                [
                    Validators.pattern(/^[a-zA-Z0-9_\-\.@]+$/),
                ],
            )
        },
            this.passwordValidator('password', 'confirmPassword'),
        );
        
        const emailId = localStorage.getItem('emailId');
        this.email = emailId && emailId !== undefined && emailId !== 'undefined' ? emailId : '';
        
        this.loginForm = new FormGroup({
            email: new FormControl(
                this.email,
                [
                    Validators.required,
                    Validators.email,
                ]),
            loginPassword: new FormControl(
                null,
                [
                    Validators.required,
                    Validators.minLength(8),
                    Validators.pattern(/^(?=.*[a-z])(?=.*[0-9])(?=.*[\^\$\*\.\[\]\{\}\(\)\?\|\\/&"!@#%,><':;_~`])/)
                ]),
        });

        if (this.navigateFrom) {
            this.isAuthSubscription = this.authStoreDispatcher.isAuthenticated().subscribe(val => {
                if (val) {
                    this.isAuthSubscription.unsubscribe();
                    this.close('accept');
                    this.ref.markForCheck();
                }
            });
        }

        this._routeParameterSubscription = this.route.queryParams.subscribe(params => {
            if (params['inviteCode'] && this.desktopConfig) {
                this.registrationForm.get("inviteCode").setValue(params['inviteCode']);
                this.inviteCode = params['inviteCode'];
                this.isSignUp = true;
                this.ref.detectChanges();
            }
        });
        
        this.facebookLoginSubscription = this.featureService.facebookLoginEnabled$.subscribe((facebookLoginEnabled) => {
            this.facebookLoginEnabled = facebookLoginEnabled;
            this.ref.detectChanges();
        });

        this.ref.detectChanges();
    }

    private setAcceptTermsOnRegistrationPage() {
        if (this.acceptPrivacy.value && this.acceptTerms.value) {
            this.profileDispatcher.acceptTermsOnRegistrationPage();
        }
    }

    passwordValidator(controlName: string, checkControlName: string): ValidatorFn {
        return (controls: AbstractControl): { [key: string]: boolean } => {
            const control = controls.get(controlName);
            const checkControl = controls.get(checkControlName);
            
            if (control.value === null) return null;

            if (control.value === '') {
                controls.get(checkControlName)?.setErrors(null);
                return null;
            }

            if (control?.value !== checkControl?.value)
                controls.get(checkControlName)?.setErrors({ matching: true });
            else
                controls.get(checkControlName)?.setErrors(null);
            
            return null;
        }
    }

    async facebookLogin() {

        const validInviteCode = await this.validateInviteCode();
        if (!validInviteCode) return;
        this.setAcceptTermsOnRegistrationPage();
        const isAnonymous = await this.authStoreDispatcher.isAnonymous();
        if (this.isQuickplay && isAnonymous) {
            if (!this.acceptPrivacy.value || !this.acceptTerms.value) {
                this.resultPopup("Need to accept Privacy Policy and Terms of service.");
            } else {
                try {
                    const canClose = await this.authService.linkWithFacebook(this.registrationForm.get("inviteCode").value);
                    if (canClose) {
                        const myID = await this.authStoreDispatcher.getMyID();
                        await this.profileService.setUserTermsToAccepted(myID);
                        this.close('accept');
                    }
                    this.navigateToLobby();
                    this.clearNicknameFromStorage();
                } catch (err) {
                    const options: SopoConfirmSettings = {
                        data: {
                            body: 'Something went wrong, \nPlease try again with another facebook account.',
                            okText: 'OK',
                        },
                        zIndex: 2001,
                    }
                    return await this.sopoPopupService.promptForConfirmation('app', options);
                }
            }
        } else {
            try {
                this.authStoreDispatcher.loginWithFacebook(this.registrationForm.get("inviteCode").value);
                if (!this.navigateFrom && this.dialogRef !== undefined) {
                    this.close('accept');
                }
            } catch (error) {
                const options: SopoConfirmSettings = {
                    data: {
                        body: 'Something went wrong, \nPlease try again with another facebook account.',
                        okText: 'OK',
                    }
                }
                return await this.sopoPopupService.promptForConfirmation('app', options);
            }
        }
    }

    async googleLogin() {

        const validInviteCode = await this.validateInviteCode();
        if (!validInviteCode) return;
        this.setAcceptTermsOnRegistrationPage();
        const isAnonymous = await this.authStoreDispatcher.isAnonymous();
        if (this.isQuickplay && isAnonymous) {
            if (!this.acceptPrivacy.value || !this.acceptTerms.value) {
                this.resultPopup("Need to accept Privacy Policy and Terms of service.");
            } else {
                try {
                    const canClose = await this.authService.linkWithGoogle(this.registrationForm.get("inviteCode").value);
                    if (canClose) {
                        const myID = await this.authStoreDispatcher.getMyID();
                        await this.profileService.setUserTermsToAccepted(myID);
                        this.close('accept');
                    }
                    this.navigateToLobby();
                    this.clearNicknameFromStorage();
                } catch (error) {
                    const options: SopoConfirmSettings = {
                        data: {
                            body: 'Something went wrong, \nPlease try again with another email address.',
                            okText: 'OK',
                        },
                        zIndex: 2001,
                    }
                    return await this.sopoPopupService.promptForConfirmation('app', options);
                }
            }
        }
        else {
            try {
                this.authStoreDispatcher.loginWithGoogle(this.registrationForm.get("inviteCode").value);
                if (!this.navigateFrom && this.dialogRef !== undefined) {
                    this.close('accept');
                }
            } catch (error) {
                const options: SopoConfirmSettings = {
                    data: {
                        body: 'Something went wrong, \nPlease try again with another email address.',
                        okText: 'OK',
                    }
                }
                return await this.sopoPopupService.promptForConfirmation('app', options);
            }
        }
    }

    async walletLogin(wallet: SupportedWallets ) {


        const validInviteCode = await this.validateInviteCode();
        if (!validInviteCode) return;

        this.setAcceptTermsOnRegistrationPage();

        const isAnonymous = await this.authStoreDispatcher.isAnonymous();
        if (this.isQuickplay && isAnonymous) {
            if (!this.acceptPrivacy.value || !this.acceptTerms.value) {
                this.resultPopup("Need to accept Privacy Policy and Terms of service.");
                return;
            }

            try {
                const canClose = await this.authService.linkWithWallet(wallet, this.registrationForm.get("inviteCode").value);
                if (canClose) {
                    const myID = await this.authStoreDispatcher.getMyID();
                    await this.profileService.setUserTermsToAccepted(myID);
                    this.close('accept');
                }
                this.navigateToLobby();
                this.clearNicknameFromStorage();
            } catch (error) {
                const options: SopoConfirmSettings = {
                    data: {
                        body: error.message ?? `Something went wrong, \nPlease try again with another ${wallet} account.`,
                        okText: 'OK',
                    },
                    zIndex: 2001,
                }
                return await this.sopoPopupService.promptForConfirmation('app', options);
            }
        } else {
            this.authStoreDispatcher.loginWithWallet(this.registrationForm.get("inviteCode").value, wallet);
        }
    }

    displayLoginForm() {
        this.islogin = true;
    }

    async emailLogin() {

        if (this.loginForm.controls['email'].valid && this.loginForm.controls['loginPassword'].value?.length >= 6){
            const pattern: RegExp = /^(?=.*[a-z])(?=.*[0-9])(?=.*[\^\$\*\.\[\]\{\}\(\)\?\|\\/&"!@#%,><':;_~`])(?=.{8,}$)/;
            let hasWeakPassword = false;
            if (!pattern.test(this.loginForm.controls['loginPassword'].value)){
                localStorage.setItem('hasWeakPassword', 'true');
                hasWeakPassword = true;
            }else{
                localStorage.removeItem('hasWeakPassword');
                hasWeakPassword = false;
            }
            this.authStoreDispatcher.login(this.loginForm.value.email, this.loginForm.value.loginPassword, hasWeakPassword);
        }
        else
            this.loginErroFlag = true;
    }

    async anonymusLogin() {

        this.loadingCtrl.present('auth');
        this.guestLogin();
        this.loadingCtrl.dismiss('auth');
        this.sopoPopupService.openDialog('app', AnonymusQuickplayComponent, {
            data: {
                isGuest: true
            }
        })
    }

    async appleLogin() {

        const validInviteCode = await this.validateInviteCode();
        if (!validInviteCode) return;
        this.setAcceptTermsOnRegistrationPage();
        const isAnonymous = await this.authStoreDispatcher.isAnonymous();
        if (this.isQuickplay && isAnonymous) {
            if (!this.acceptPrivacy.value || !this.acceptTerms.value) {
                this.resultPopup("Need to accept Privacy Policy and Terms of service.");
            } else {
                try {
                    const canClose = await this.authService.linkWithApple(this.registrationForm.get("inviteCode").value);
                    if (canClose) {
                        const myID = await this.authStoreDispatcher.getMyID();
                        await this.profileService.setUserTermsToAccepted(myID);
                        this.close('accept');
                    }
                    this.navigateToLobby();
                    this.clearNicknameFromStorage();
                } catch (err) {
                    const options: SopoConfirmSettings = {
                        data: {
                            body: 'Something went wrong, \nPlease try again with another apple account.',
                            okText: 'OK',
                        },
                        zIndex: 2001,
                    }
                    return await this.sopoPopupService.promptForConfirmation('app', options);
                }
            }
        } else {
            try {
                this.authStoreDispatcher.loginWithApple(this.registrationForm.get("inviteCode").value);
                if (!this.navigateFrom && this.dialogRef !== undefined) {
                    this.close('accept');
                }
            } catch (error) {
                const options: SopoConfirmSettings = {
                    data: {
                        body: 'Something went wrong, \nPlease try again with another apple account.',
                        okText: 'OK',
                    }
                }
                return await this.sopoPopupService.promptForConfirmation('app', options);
            }
        }
    }

    guestLogin() {
        this.zone.run(async() => {
            const dialogRef = this.sopoPopupService.openDialog('app', GuestLoginPopupComponent, { sCss: ['sopo-popup-guest-login-container'] });
            const answer = await firstValueFrom(dialogRef.afterClosed);
            if (!answer) return;
            const isAuth = await firstValueFrom(this.authStoreDispatcher.isAuthenticated());
            if (isAuth) return;
            return this.authStoreDispatcher.signInAnonymously();
        })
    }

    displaySignUp() {
        this.isSignUp = true;
        this.islogin = false;
        this.registrationErrorFlag = false;
        this.registrationForm.reset();
        if (this.inviteCode) this.registrationForm.get('inviteCode').setValue(this.inviteCode);
    }

    displayLogin() {
        this.isSignUp = false;
        this.islogin = false;
        const emailId = localStorage.getItem('emailId');
        this.email = emailId && emailId !== undefined && emailId !== 'undefined' ? emailId : '';
        this.loginErroFlag = false;
        this.registrationForm.get('inviteCode').setValue(null)
    }

    public async signUp(): Promise<boolean> {

        const isAnonymous = await this.authStoreDispatcher.isAnonymous();
        const validInviteCode = await this.validateInviteCode();
        if (!validInviteCode) return;
        if ( this.registrationForm.valid && this.acceptPrivacy.value && this.acceptTerms.value ) {
            if (this.isQuickplay && isAnonymous) {
                try {
                    this.loadingCtrl.present('auth');
                    const canClose = await this.authService.linkWithEmail(this.registrationForm.value['email'], this.registrationForm.value['password'], this.registrationForm.get("inviteCode").value);
                    if (canClose) this.close('accept');
                    this.loadingCtrl.dismiss('auth');
                    this.navigateToLobby();
                    this.clearNicknameFromStorage();
                } catch(error) {
                    this.loadingCtrl.dismiss('auth');
                    const options: SopoConfirmSettings = {
                        data: {
                            body: 'Something went wrong, \nPlease try again with another email address.',
                            okText: 'OK',
                        },
                        zIndex: 2001,
                    }
                    this.registrationForm.reset();
                    return await this.sopoPopupService.promptForConfirmation('app', options);
                }
            } 
            else {
                try {
                    this.authStoreDispatcher.signup(this.registrationForm.value.email, this.registrationForm.value.password, this.registrationForm.get("inviteCode").value);
                    if (!this.navigateFrom && this.dialogRef !== undefined) {
                        this.close('accept');
                    }
                } catch (error) {
                    const options: SopoConfirmSettings = {
                        data: {
                            body: 'Something went wrong, \nPlease try again with another email address.',
                            okText: 'OK',
                        }
                    }
                    this.registrationForm.reset();
                    return await this.sopoPopupService.promptForConfirmation('app', options);
                }
            }
        } else
            this.registrationErrorFlag = true;
    }

    navigateToLobby() {
        if (this.isNavigateToLobby) {
            this.navCtrl.navigateForward('lobby/home', { state: { fromLogin: true }});
            this.isNavigateToLobby = false;
        }
    }

    forgotPassword() {
        if (!this.navigateFrom) {
            this.sopoPopupService.clearViewContainer('app');
            this.sopoPopupService.openDialog('app', ForgotPasswordComponent, {
                data: {},
            });
        }
    }

    openAppBrowser(url: string): void {
        if (isElectron()) AppLauncherElectron.openUrl({ url });
        else this.iab.create(url, '_system');
    }

    openAgreeComponent(type: 'PP' | 'TOS') {
        this.zone.run(async() => {
            let dialog = this.matDialog.open(AgreePrivacyComponent, {
                data: type,
                panelClass: 'agree-component-dialog'
            });
            this.termsOpened(true);
            await firstValueFrom(dialog.afterClosed()).then(answer => {
                this.termsOpened(false);
                if (answer === undefined) return;
                switch (type) {
                    case 'PP':
                        this.acceptPrivacy.next(answer);
                        break;
                    case 'TOS':
                        this.acceptTerms.next(answer);
                        break;
                    default:
                        return;
                }
            })
            this.ref.detectChanges();
        });
    }

    close(value?: string): void {
        if (!this.dialogRef) return;
        switch(value) {
            case 'reject':
                this.dialogRef.close('reject');
                break;
            case 'accept':
                this.dialogRef.close('accept');
                break;
            default:
                this.dialogRef.close();
        }
    }

    async resultPopup(text: string): Promise<boolean> {
        const options: SopoConfirmSettings = {
            data: {
                body: text,
                okText: 'OK',
            },
            zIndex: 2001,
        }
        return await this.sopoPopupService.promptForConfirmation('app', options);
    }

    async toastError(message: string): Promise<void> {
        const toast = await this.toastCtrl.create({
            message,
            duration: 3000,
            position: 'bottom' 
        });
        await toast.present();
    }

    termsOpened(flag: boolean) {
        if (this.isQuickplay) {
            const tab = document.querySelector(".segment-wrapper-tab") as HTMLDivElement;
            if (tab) {
                if (flag) tab.style.zIndex = "1000001";
                else tab.style.zIndex = "1000002";
            }
        }
    }

    async validateInviteCode(): Promise<boolean> {
        const inviteCodeControl = this.registrationForm.get('inviteCode');
        if (inviteCodeControl.invalid) return false;
        const validInviteCode: boolean | null = await this.inviteCodeService.validateInviteCode(inviteCodeControl.value);
        if (!validInviteCode) {
            validInviteCode === false ? inviteCodeControl.setErrors({ validity: true}) : inviteCodeControl.setErrors({ network: true});
            this.ref.detectChanges();
            return false;
        };
        return true;
    }

    async clearNicknameFromStorage() {
        const playerId = await this.authStoreDispatcher.getMyID();
        localStorage.removeItem(`nicknameSet_${playerId}`);
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        if (this.authSubscribe) {
            this.authSubscribe.unsubscribe();
        }
        if (this.isAuthSubscription) this.isAuthSubscription.unsubscribe();
        if (this._routeParameterSubscription) this._routeParameterSubscription.unsubscribe();
    }
}
